import type { TeamMember } from '../types/teamMember'

/**
 * Resolves the SingleSelect UUID for a stored `{ id, name }` person reference
 * (where `id` is the team member's external id). Used by every dropdown that
 * persists a snapshot of a person at edit time:
 *
 *   - Floor Sheet operator slots (via the floorSheetOperatorTeamMembers
 *     wrapper that calls this and continues to expose the legacy alias).
 *   - PQR preview signatories (Prepared By / Reviewed By).
 *
 * Match precedence: external id + name first (catches a member whose external
 * id was reassigned to someone else), then external id alone (catches a
 * legitimate display-name change). Returns '' when nothing matches so the
 * caller can render a "ghost" placeholder for deactivated/deleted members.
 */
export function resolveMemberUuidForStoredPerson(
  stored: { name: string; id: string; memberId?: string },
  candidates: TeamMember[]
): string {
  const uuid = stored.memberId?.trim()
  if (uuid) {
    const byUuid = candidates.find((m) => m.id === uuid)
    if (byUuid) return byUuid.id
  }
  const extId = stored.id.trim()
  if (!extId) {
    const name = stored.name.trim()
    if (!name) return ''
    const byName = candidates.filter((m) => m.name === name)
    return byName.length === 1 ? byName[0].id : ''
  }
  const byIdAndName = candidates.find(
    (m) => m.memberExternalId === extId && m.name === stored.name
  )
  if (byIdAndName) return byIdAndName.id
  const byId = candidates.find((m) => m.memberExternalId === extId)
  return byId?.id ?? ''
}
